/**
 * LibraryView — top-level Library route component.
 *
 * Acceptance criteria:
 *   1. `/app` lands on this component on both mobile and desktop.
 *   2. Renders a single unconditional Recipes view — no tab strip, no Batches tab.
 *      (The Batches UI was removed in the 2026-07-10 dead-UI cleanup sweep; the
 *      `batch.*` API + export-format support remain. See `.plan/wishlist/batches-ui.md`.)
 *   3. Empty-Library state shows the CTA chip when there are zero recipes.
 *
 * Design refs:
 *   design.md §6 — Library route, collection chips.
 *   design.md §2 — accent reserved for go-to pills + CTAs; hairlines only.
 *   design.md §6.0a — discoverability rule.
 *   design.md §6.3 Amendment 2026-05-10 — tags replace Collection nav on landing;
 *     LibraryGridView + TagFilterChips replace the old FlatRecipeList landing.
 *   branding.md §5 / §8 — brand voice; banned words.
 *   A.3.1 / A.3.2 / A.3.3 / A.3.5 (gap doc) — Library build spec.
 *   Frames 1, 9, 21 — populated mobile, populated desktop, empty state.
 *
 * Wave 2 migration note:
 *   LibraryGridView + TagFilterChips now render the Recipes view.
 *   FlatRecipeListProps fields are kept as Partial so app.tsx compiles without
 *   changes during migration. Wave 3: remove Partial<FlatRecipeListProps> once
 *   app.tsx is updated to LibraryViewProps.
 *
 * CLAUDE.md Rule 7 — mobile + tablet + desktop from first commit.
 * CLAUDE.md Rule 8 — any fetch errors surface inline.
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
// N2: useSearchParams removed — tab routing removed with Batches tab.
// Batches UI (BatchesTab / BatchDetailView) deleted in the 2026-07-10 dead-UI
// cleanup sweep; the `batch.*` API + export-format support remain.
import { DraftsDrawer } from "./DraftsDrawer";
import { EmptyLibraryStateWithPip as EmptyLibraryState } from "./EmptyLibraryState";
import { LibraryGridView } from "./LibraryGridView";
import type { FlatRecipeListProps } from "~/features/explorer/FlatRecipeList";
// PERF Wave 3a — F4 + F9: chunk prefetch and idle data precache.
import { prefetchChunksOnIdle } from "~/lib/prefetch-chunks";
// family-groups (plan 2026-07-02 §4.5) — the in-Library Groups section.
import { LibraryGroupsSection } from "~/features/groups/LibraryGroupsSection";
import type { GroupsApi } from "~/features/groups/api";

// ── Types ─────────────────────────────────────────────────────────────────────

// N2: Tab type removed — Batches tab hidden; single recipes view only.

/**
 * Wave 2 migration: LibraryView no longer renders FlatRecipeList / CollectionChips
 * on the landing (replaced by LibraryGridView + TagFilterChips per design.md §6.3
 * Amendment 2026-05-10). All FlatRecipeListProps fields are kept as Partial so
 * app.tsx can continue passing them without TypeScript errors during migration.
 * Wave 3: remove the Partial<FlatRecipeListProps> spread once app.tsx is updated.
 */
export interface LibraryViewProps extends Partial<FlatRecipeListProps> {
  /**
   * Required: the recipe list is always needed for the landing view.
   * Override Partial<FlatRecipeListProps>.recipes to make it required.
   */
  recipes: FlatRecipeListProps["recipes"];
  /**
   * Cold-load empty-state flash fix — whether the initial recipe.list fetch has
   * settled. While "loading" we render NEITHER the empty-state nor the grid: a
   * populated account would otherwise flash the empty-state (Pip + "Your library
   * is empty.") for the fetch latency because `recipes` starts []. Defaults to
   * "ready" so standalone renders / tests that pass recipes directly behave as
   * before (no skeleton gate).
   */
  recipesLoadState?: "loading" | "ready";
  /**
   * Required: UUID of the currently active/selected recipe, or null.
   * Override Partial<FlatRecipeListProps>.activeRecipeUuid to make it required.
   */
  activeRecipeUuid: FlatRecipeListProps["activeRecipeUuid"];
  /**
   * Required: called when a recipe card is tapped/clicked.
   * Override Partial<FlatRecipeListProps>.onActivateRecipe to make it required.
   */
  onActivateRecipe: FlatRecipeListProps["onActivateRecipe"];
  /**
   * oRPC API client — forwarded to the DraftsDrawer for its draft.list call.
   * Typed as `any` following the pattern in routes/app.tsx.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  apiClient: any;
  /** Called when the empty-Library CTA is tapped — opens new-recipe flow. */
  onCreateRecipeFromEmpty?: () => void;
  /**
   * Beat-0 entry fork (onboarding revival T3) — the "Open a worked example" arm
   * of the empty-library state. Adopts the napoletana seed via `recipe.spinOff`
   * and navigates into the new recipe. When absent, the arm is not rendered.
   * The parent (app.tsx) owns the RPC + the governed first-recipe Pip/toast; the
   * empty-state component owns the busy state + visible failure (Rule 8).
   */
  onOpenWorkedExample?: () => Promise<void>;
  /**
   * Count of active drafts across all recipes (Wave 2).
   * When > 0, an "N drafts" chip is rendered in the title row.
   * When 0 or absent, the chip is NOT rendered (§2a: no ghost space).
   */
  draftsCount?: number;
  /**
   * Called when the "N drafts" chip is clicked (Wave 2 entry point).
   * Wave 5: wired to open the DraftsDrawer.
   * When absent, the drawer is still opened via local state.
   */
  onDraftsChipClick?: () => void;
  /**
   * Called when the user taps a draft row in the DraftsDrawer.
   * The parent (app.tsx WorkspaceContainer) activates the recipe +
   * sets the doc key to navigate to the draft editor.
   *
   * When absent, the DraftsDrawer is not rendered (drawer requires
   * navigation wiring to be useful).
   */
  onNavigateToDraft?: (recipeUuid: string, variationUuid: string) => void;
  /**
   * family-groups (plan 2026-07-02 §4.5) — the typed GroupsApi wrapper. When
   * provided together with onOpenGroup, the "Groups" section renders below the
   * recipe grid (list + `New group`). Omitting it (fixtures/tests) hides the
   * section entirely — no group network calls fire.
   */
  groupsApi?: GroupsApi;
  /** Navigate to a group page (`/app/g/<uuid>`) — required for the Groups section. */
  onOpenGroup?: (groupUuid: string) => void;
}

// ── Styles ────────────────────────────────────────────────────────────────────

// H7 (2026-05-09): Library scrolls as ONE continuous page. The page-level
// scroll lives on the outer `library-view-shell` wrapper in app.tsx; this
// root lays out as a natural-height block. `minHeight: 100%` fills
// the shell when content is short (empty-state CTA still centred), but lets
// content grow taller than the shell when populated. `overflowY: hidden`
// is removed so the shell — not this element — owns scrolling.
const viewStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  width: "100%",
  minHeight: "100%",
  fontFamily: "var(--font-sans)",
  background: "var(--surface-base, var(--bg))",
};

const titleRowStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "10px",
  padding: "var(--space-4, 16px) var(--space-3, 12px) var(--space-2, 8px)",
  flexShrink: 0,
};

const titleStyle: React.CSSProperties = {
  fontFamily: "var(--font-sans)",
  /*
   * Bug D fix: 22px → 26px for mobile readability.
   * design.md §2 — no pure #000; warm tokens only.
   * CLAUDE.md Rule 7 — mobile readability from first commit.
   */
  fontSize: "26px",
  fontWeight: 600,
  color: "var(--text)",
  letterSpacing: "-0.02em",
  lineHeight: 1.2,
};

// Title-row "New recipe" — the contextual create affordance for the POPULATED
// library (nav-chrome-contextual-create §4.2 / §7). Before this, once the
// polymorphic "+" pill was removed the populated view had NO create entry point;
// only the empty state offered one. This is the quiet-ghost secondary-action
// idiom (design.md §2): no fill, --text colour, 1px --line hairline — the accent
// stays reserved for the drafts chip / go-to pills on this surface. A leading
// "+" is now unambiguous because it sits directly against the "New recipe"
// label (the ambiguity the polymorphic "+" caused is what this feature kills).
const newRecipeButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  // Push to the trailing edge so it never crowds the title / drafts chip.
  marginLeft: "auto",
  height: "36px",
  padding: "0 12px",
  borderRadius: "var(--radius-pill, 999px)",
  fontFamily: "var(--font-sans)",
  fontSize: "13px",
  fontWeight: 500,
  color: "var(--text)",
  background: "none",
  border: "1px solid var(--line, #dcd7cc)",
  cursor: "pointer",
  whiteSpace: "nowrap",
  flexShrink: 0,
  transition: "border-color 200ms ease, color 200ms ease",
  WebkitTapHighlightColor: "transparent",
};

// The "+" glyph — quiet, same weight/colour as the label (not an accent signal).
const newRecipeGlyphStyle: React.CSSProperties = {
  fontSize: "15px",
  lineHeight: 1,
  color: "var(--muted, #7a7266)",
};

// Scoped hover / focus / coarse-pointer rules for the title-row create control.
// Mirrors the EmptyLibraryState injection pattern (LibraryView is otherwise
// inline-styled and has no :hover hook). design.md §2 — accent focus ring; Rule
// 7 — 44px tap floor on touch, tighter on fine pointers.
const LIBRARY_TITLE_CSS = `
  .proof-library-new-recipe:hover {
    border-color: var(--text);
  }
  .proof-library-new-recipe:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
  @media (pointer: coarse) {
    .proof-library-new-recipe {
      min-height: 44px;
    }
  }
`;

if (typeof document !== "undefined") {
  const id = "proof-library-title-css";
  if (!document.getElementById(id)) {
    const el = document.createElement("style");
    el.id = id;
    el.textContent = LIBRARY_TITLE_CSS;
    document.head.appendChild(el);
  }
}

// N2: tabStripStyle + tabButtonStyle removed — Batches tab hidden.

// H7 (2026-05-09): contentAreaStyle no longer owns its own scroll — the
// page-level scroll lives on the `library-view-shell` wrapper in app.tsx.
// Keeping `flex: 1 1 0` lets this region expand to fill remaining shell
// space when content is short (so the empty-state CTA still centres), but
// the natural-height flow above means the recipe list scrolls as one document.
const contentAreaStyle: React.CSSProperties = {
  flex: "1 1 0",
  display: "flex",
  flexDirection: "column",
};

// ── Library-loading skeleton (hairline outlines, no shimmer) ────────────────────
// Three quiet card outlines that hold the library shape while recipe.list is in
// flight. design.md §2 — hairlines not shadows, no decorative animation.
const librarySkeletonListStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "10px",
  padding: "var(--space-3, 12px)",
};

const librarySkeletonCardStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  gap: "12px",
  padding: "14px 16px",
  border: "1px solid var(--line, #dcd7cc)",
  borderRadius: "12px",
  background: "var(--surface, #fdfcfa)",
  minHeight: "44px",
};

const librarySkeletonEmojiStyle: React.CSSProperties = {
  flexShrink: 0,
  width: "40px",
  height: "40px",
  borderRadius: "8px",
  background: "var(--bg, #f7f5f1)",
};

const librarySkeletonBodyStyle: React.CSSProperties = {
  flex: "1 1 0",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  paddingTop: "4px",
};

const librarySkeletonTitleBarStyle: React.CSSProperties = {
  height: "10px",
  width: "60%",
  borderRadius: "4px",
  background: "var(--line, #dcd7cc)",
};

const librarySkeletonMetaBarStyle: React.CSSProperties = {
  height: "8px",
  width: "35%",
  borderRadius: "4px",
  background: "var(--line, #dcd7cc)",
};

// N2: TAB_FOCUS_CSS + injection removed — tab buttons removed.

// ── Component ─────────────────────────────────────────────────────────────────

export function LibraryView({
  recipes,
  recipesLoadState = "ready",
  // FlatRecipeListProps backward-compat — destructured with _ prefix to suppress
  // unused-variable lint; app.tsx still passes these during the migration period.
  // Wave 3: remove once app.tsx is fully migrated to LibraryViewProps.
  //
  // collections-revival Wave 2: `collections` + `onCreateCollection` are now
  // CONSUMED — forwarded to LibraryGridView's COLLECTIONS filter row (no longer
  // unused, so un-prefixed).
  collections,
  activeRecipeUuid,
  onActivateRecipe,
  onSelectVariation: _onSelectVariation,
  selectedVariationUuid: _selectedVariationUuid,
  onSaveMemberships: _onSaveMemberships,
  onCreateCollection,
  // collections-revival Wave 4: rename/delete are now CONSUMED — forwarded to
  // LibraryGridView → LibraryCollectionChips' per-chip manage kebab (no longer
  // unused, so un-prefixed).
  onRenameCollection,
  onDeleteCollection,
  onSpinOff: _onSpinOff,
  onCreateRecipe,
  onShareLink: _onShareLink,
  onExportRecipe: _onExportRecipe,
  onValues: _onValues,
  onRenameRecipe: _onRenameRecipe,
  onDeleteRecipe: _onDeleteRecipe,
  onPrefetchRecipe: _onPrefetchRecipe,
  apiClient,
  onCreateRecipeFromEmpty,
  onOpenWorkedExample,
  draftsCount = 0,
  onDraftsChipClick,
  onNavigateToDraft,
  groupsApi,
  onOpenGroup,
}: LibraryViewProps) {
  // SPA navigation — passed to the first-run callout's "Syntax reference" link
  // so it navigates inside the SPA instead of doing a full-page reload, matching
  // the pattern used by app.tsx, Workspace.tsx, and UserMenu (all use navigate).
  const navigate = useNavigate();

  // ── F4 — Chunk prefetch on Library mount (idle priority) ─────────────────
  // Fire import() side-effects for the Workspace + TipTapEditor chunks so the
  // browser starts caching them before the user taps a recipe. Uses
  // requestIdleCallback (with setTimeout fallback) so it does not compete with
  // critical-path resources. Gates on saveData + slow-2g per plan §3a.3.
  //
  // idlePrefetchFiredRef guards against React StrictMode double-mount in dev
  // firing the requestIdleCallback twice. Module-level flags inside
  // prefetch-chunks.ts provide an additional guard at the import() level, but
  // the ref prevents even the requestIdleCallback registration from firing
  // twice on the same LibraryView mount.
  const idlePrefetchFiredRef = useRef(false);
  useEffect(() => {
    if (idlePrefetchFiredRef.current) return;
    idlePrefetchFiredRef.current = true;
    prefetchChunksOnIdle();
  }, []); // intentionally empty — fires once on mount only

  // ── F9 — Idle-time top-3 recipe data precache ─────────────────────────────
  // After recipes are populated, fire low-priority recipe.get calls for the
  // top 3 most-recent entries. They land in the SWR / IDB cache so the first
  // tap resolves from warm cache instead of paying a network round trip.
  //
  // "Top 3" = recipes[0..2]: recipe.list returns rows ordered updated_at DESC,
  // so the first 3 are always the most recently touched. No new server call.
  //
  // Reuses `onPrefetchRecipe` (the cachedApi.recipe.get wrapper from #168) so
  // the result lands in the same SWR IDB cache that the click path reads from.
  //
  // idlePrecacheFiredRef prevents the effect from re-running if the recipes
  // array reference changes (e.g. after a collection-enrichment pass). The
  // top-3 by recency are already the most valuable to precache; fetching them
  // again on a re-render is wasteful.
  const idlePrecacheFiredRef = useRef(false);
  useEffect(() => {
    if (idlePrecacheFiredRef.current) return;
    if (!_onPrefetchRecipe) return;
    if (recipes.length === 0) return;
    idlePrecacheFiredRef.current = true;

    const top3UuidsSnapshot = recipes.slice(0, 3).map((r) => r.uuid);
    const prefetchSnapshot = _onPrefetchRecipe;

    // Check saveData at schedule time.
    let saveDataEnabled = false;
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      saveDataEnabled = (navigator as any).connection?.saveData === true;
    } catch {
      // Save-Data feature detection unsupported — treat as not enabled.
      saveDataEnabled = false;
    }
    if (saveDataEnabled) return;

    let cancelled = false;

    const doFetch = () => {
      if (cancelled) return;
      // Fire each prefetch; SWR layer dedupes any in-flight or fresh hits.
      // warmBody:false — this idle precache warms chunks + recipe.get only. The
      // draft-body SWR entry has a 15s TTL that would lapse long before any click,
      // so warming it here is dead-weight DO load (hover/pointerdown warms it at
      // the moment intent is expressed, when the TTL is still live).
      for (const uuid of top3UuidsSnapshot) {
        if (cancelled) break;
        prefetchSnapshot(uuid, { warmBody: false });
      }
    };

    let rafId: ReturnType<typeof requestIdleCallback> | undefined;
    if (typeof requestIdleCallback !== "undefined") {
      rafId = requestIdleCallback(doFetch, { timeout: 3000 });
    } else {
      // Safari < 16.4 fallback.
      const tid = setTimeout(doFetch, 2000);
      return () => {
        cancelled = true;
        clearTimeout(tid);
      };
    }
    return () => {
      cancelled = true;
      if (rafId !== undefined && typeof cancelIdleCallback !== "undefined") {
        cancelIdleCallback(rafId);
      }
    };
  }, [recipes, _onPrefetchRecipe]); // re-runs if recipes populate from [] to non-empty

  // Drafts drawer open state — Wave 5 wiring.
  const [draftsDrawerOpen, setDraftsDrawerOpen] = useState(false);

  // Drafts chip click handler — opens the DraftsDrawer (Wave 5).
  function handleDraftsChipClick() {
    if (onDraftsChipClick) {
      onDraftsChipClick();
    }
    // Always open the local drawer — onDraftsChipClick is an optional
    // additional callback (e.g. analytics). The drawer state is local.
    setDraftsDrawerOpen(true);
  }

  // N2: tab routing removed — single recipes-only view.
  // Cold-load flash fix: the initial recipe.list fetch is still in flight →
  // render neither empty-state nor grid (a sub-second blank beats a false
  // "empty" for a populated account whose fetch has not resolved).
  const isLoading = recipesLoadState === "loading";
  // Derive empty-library state: settled fetch AND zero recipes. A loading fetch
  // is NEVER "empty" (mirrors the bodyLoadState "loading" ≠ "empty" rule).
  const isEmpty = !isLoading && recipes.length === 0;

  const handleCreateFromEmpty = useCallback(() => {
    if (onCreateRecipeFromEmpty) {
      // The parent's `onCreateRecipeFromEmpty` is sync `() => void` and owns
      // its own error surface (Rule 8 — toast on failure). Just fire it.
      onCreateRecipeFromEmpty();
    } else if (onCreateRecipe) {
      // P0-7: the parent's `onCreateRecipe` opens the route-level new-recipe
      // modal (required human name) and resolves immediately; the actual create
      // runs on modal submit, where rejections surface inline (Rule 8). This
      // .catch keeps the click handler fire-and-forget while logging the rare
      // case where opening the modal rejects, so the click is never silently
      // dropped.
      onCreateRecipe().catch((err) => {
        console.error("Opening the new-recipe form failed", err);
      });
    }
  }, [onCreateRecipeFromEmpty, onCreateRecipe]);

  // Populated-library title-row "New recipe" click. Always the standard
  // new-recipe modal (required human name → recipe.create), never the Beat-0
  // empty-state fork. onCreateRecipe opens the route-level modal and resolves
  // immediately; the create runs on modal submit where rejections surface
  // inline (Rule 8). This .catch keeps the tap fire-and-forget while logging the
  // rare modal-open rejection — never a bare catch, never a silent drop (Rule 8).
  const handleNewRecipeClick = useCallback(() => {
    if (!onCreateRecipe) return;
    onCreateRecipe().catch((err) => {
      console.error("Opening the new-recipe form failed", err);
    });
  }, [onCreateRecipe]);

  return (
    <div style={viewStyle} data-testid="library-view">
      {/* Title row — "Library" + optional drafts chip */}
      <div style={titleRowStyle}>
        <h1 style={titleStyle}>Library</h1>
        {/*
         * §2a: drafts chip ONLY when draftsCount > 0. No ghost space when 0.
         * Ember accent — current-variation indicator equivalent (design.md §2).
         * Wave 5 drawer wires here; this chip is the entry point for Wave 2.
         */}
        {draftsCount > 0 && (
          <button
            type="button"
            onClick={handleDraftsChipClick}
            aria-label={`${draftsCount} active ${draftsCount === 1 ? "draft" : "drafts"}`}
            data-testid="drafts-chip"
            style={{
              display: "inline-flex",
              alignItems: "center",
              height: "26px",
              padding: "0 10px",
              borderRadius: "999px",
              fontSize: "12px",
              fontFamily: "var(--font-sans, 'Inter', sans-serif)",
              fontVariantNumeric: "tabular-nums",
              lineHeight: "1",
              whiteSpace: "nowrap",
              cursor: "pointer",
              flexShrink: 0,
              border: "1px solid var(--line, #dcd7cc)",
              background: "color-mix(in srgb, var(--accent, #c23d12) 8%, transparent)",
              color: "var(--accent, #c23d12)",
              transition: "background 200ms ease",
            }}
          >
            {draftsCount} {draftsCount === 1 ? "draft" : "drafts"}
          </button>
        )}
        {/*
         * Contextual "New recipe" — the populated library's create entry point
         * (nav-chrome-contextual-create §4.2). Quiet-ghost hairline, no accent
         * fill; label is the sole affordance (banned-word-free, no exclamation).
         * Guarded on onCreateRecipe so the harness/empty routes that omit it
         * render no dead control.
         */}
        {onCreateRecipe && (
          <button
            type="button"
            onClick={handleNewRecipeClick}
            className="proof-library-new-recipe"
            style={newRecipeButtonStyle}
            data-testid="library-new-recipe"
          >
            <span aria-hidden="true" style={newRecipeGlyphStyle}>
              +
            </span>
            New recipe
          </button>
        )}
      </div>

      {/* N2: Tab strip removed — Batches hidden; recipes-only view.
          Content area renders unconditionally; no tabpanel role needed. */}
      <div style={contentAreaStyle}>
        {isLoading ? (
          /*
           * Initial recipe.list still in flight — render skeleton card outlines
           * (not the empty-state, not the grid). A populated account starts with
           * `recipes === []`, so painting the empty-state here would flash a
           * false "Your library is empty." for the fetch latency. Three hairline
           * card outlines (no shimmer — design.md §2 quiet register) hold the
           * shape until the fetch resolves. aria-busy matches the ReadView
           * loading idiom so screen readers get the busy cue.
           */
          <div
            data-testid="library-loading"
            aria-busy="true"
            aria-label="Loading recipes"
            style={librarySkeletonListStyle}
          >
            {[0, 1, 2].map((i) => (
              <div key={i} style={librarySkeletonCardStyle} aria-hidden="true">
                <div style={librarySkeletonEmojiStyle} />
                <div style={librarySkeletonBodyStyle}>
                  <div style={librarySkeletonTitleBarStyle} />
                  <div style={librarySkeletonMetaBarStyle} />
                </div>
              </div>
            ))}
          </div>
        ) : isEmpty ? (
          /* Empty-Library state (Frame 21) — shown when user has zero recipes. */
          <EmptyLibraryState
            onCreateRecipe={handleCreateFromEmpty}
            onOpenSyntaxReference={() => void navigate("/app/help")}
            {...(onOpenWorkedExample !== undefined ? { onOpenWorkedExample } : {})}
          />
        ) : (
          /*
           * Populated recipes view — Wave 2 R4.x library grid with tag chips.
           * design.md §6.3 Amendment 2026-05-10: tags replace Collection
           * navigation on the landing. FlatRecipeList + CollectionChips remain
           * in the desktop sidebar explorer (not rendered here).
           */
          <LibraryGridView
            recipes={recipes}
            activeRecipeUuid={activeRecipeUuid}
            onActivateRecipe={onActivateRecipe}
            {...(collections !== undefined ? { collections } : {})}
            {...(onCreateCollection !== undefined ? { onCreateCollection } : {})}
            {...(onRenameCollection !== undefined ? { onRenameCollection } : {})}
            {...(onDeleteCollection !== undefined ? { onDeleteCollection } : {})}
            {...(_onPrefetchRecipe !== undefined ? { onPrefetchRecipe: _onPrefetchRecipe } : {})}
          />
        )}
      </div>

      {/*
       * family-groups (plan 2026-07-02 §4.5) — Groups section inside the Library
       * surface. Rendered once the initial recipe.list settles (not during the
       * loading skeleton) and only when the shell wires the GroupsApi + navigate.
       * Self-fetches its own list; padded to match the library gutters. Placement
       * pending instrument-design-reviewer sign-off (plan amendment 8).
       */}
      {!isLoading && groupsApi && onOpenGroup && (
        <div style={{ padding: "0 var(--space-3, 12px) var(--space-6, 24px)" }}>
          <LibraryGroupsSection api={groupsApi} onOpenGroup={onOpenGroup} />
        </div>
      )}

      {/*
       * DraftsDrawer — Wave 5.
       * Only rendered when `onNavigateToDraft` is provided (navigation wiring
       * is required for the drawer to be useful). Without it the chip would
       * open a read-only list with no tap target, which is not a useful state.
       *
       * design.md §2 — Sheet controlled API; snapPoints ["peek","full"].
       * branding.md §5.2.1 — "Drafts" header: no banned words.
       */}
      {onNavigateToDraft !== undefined && (
        <DraftsDrawer
          open={draftsDrawerOpen}
          onClose={() => setDraftsDrawerOpen(false)}
          recipes={recipes}
          apiClient={apiClient}
          onNavigateToDraft={onNavigateToDraft}
        />
      )}
    </div>
  );
}
